/**
 * ความพร้อมปิดรอบ (Close Readiness, เรื่อง 10) — checklist before locking a
 * payroll period. 🔴 items block a clean close (lock asks for an acknowledge
 * reason); 🟡 items are informational. Each links to where it gets cleared.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertCircle, AlertTriangle, Clock, Wallet } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { PayrollPipeline } from '../../components/PayrollPipeline';
import { Loading } from '../../components/layout/Loading';
import { LoadError } from '../../components/layout/LoadError';
import { useCloseReadiness, useEmployees, type ReadinessItem } from '../../hooks';

const ITEM_ROUTE: Record<string, string> = {
  forgot_payroll: '/owner/cycle#payroll',
  missing_salary_base: '/hr/employees',
  // fb#351 — unresolved = คนที่สรุปเวลาแล้วยังมีการ์ดผิดปกติค้าง (ส่วนใหญ่) หรือลาฉุกเฉินรอตัดสิน (มีแถวของตัวเองอีกแถว)
  // เดิมชี้ /approvals#emergency → HR กดเข้าไปเจอ "ไม่มีลาฉุกเฉินรอตัดสิน" ทุกครั้ง
  emergency_leave_pending_review: '/approvals#emergency',
  unresolved: '/hr/escalations',
  escalation_pending: '/hr/escalations',
  leave_pending: '/approvals',
  ot_pending: '/approvals',
  missing_summary: '/owner/cycle#reconcile',
  terminated_no_finalpay: '/owner/termination',
  net_variance: '/owner/cycle#payroll',
  unpaired_line: '/hr/employees',
  missing_start_date: '/hr/employees',
  resigned_no_end_date: '/hr/employees',
};

/** fb#351 — ชื่อคนในรายการ (detail.emp_codes) ให้เห็นเลยว่าใคร ไม่ต้องไปตามหาอีกหน้า */
function affectedCodes(it: ReadinessItem): string[] {
  const raw = (it.detail as { emp_codes?: unknown } | undefined)?.emp_codes;
  return Array.isArray(raw) ? raw.filter((c): c is string => typeof c === 'string') : [];
}
type EmpName = { emp_code: string; first_name?: string | null; last_name?: string | null; nickname?: string | null };
const NAMES_SHOWN = 10;

function Row({ it, names }: { it: ReadinessItem; names: Map<string, EmpName> }) {
  const Icon = it.severity === 'red' ? AlertCircle : AlertTriangle;
  const color = it.severity === 'red' ? 'text-red-600 border-l-red-500' : 'text-amber-600 border-l-amber-400';
  const to = ITEM_ROUTE[it.key];
  const codes = affectedCodes(it);
  const label = (c: string) => {
    const e = names.get(c);
    if (!e) return c;
    const full = [e.first_name, e.last_name].filter(Boolean).join(' ');
    return `${full || c}${e.nickname ? ` (${e.nickname})` : ''}`;
  };
  const body = (
    <div className={`flex items-start gap-3 border-l-4 ${color.split(' ')[1]} bg-white rounded-r-lg px-3 py-3`}>
      <Icon className={`w-5 h-5 shrink-0 ${color.split(' ')[0]}`} />
      <div className="flex-1 min-w-0">
        <span className="text-sm">{it.label}</span>
        {codes.length > 0 && (
          <p className="text-xs text-gray-500 mt-1">
            {codes.slice(0, NAMES_SHOWN).map(label).join(' · ')}
            {it.count > NAMES_SHOWN && ` · และอีก ${it.count - NAMES_SHOWN} คน`}
          </p>
        )}
      </div>
      <span className="text-sm font-semibold tabular-nums">{it.count}</span>
    </div>
  );
  return to ? <Link to={to} className="block hover:opacity-80">{body}</Link> : body;
}

/** Content-only version used by the PayrollCycle hub. */
export function CloseReadinessContent() {
  const [period] = useState(() => new Date().toISOString().slice(0, 7));
  const { data, isLoading, error, refetch } = useCloseReadiness(period);
  const { data: emps } = useEmployees() as { data: EmpName[] | undefined };
  const names = new Map((emps ?? []).map((e) => [e.emp_code, e]));
  if (isLoading) return <Loading />;
  if (error || !data) return <LoadError error={error} onRetry={() => refetch()} />;

  const hard = data.items.filter((i) => i.hardBlock && i.count > 0);
  const red = data.items.filter((i) => i.affectsCycle && !i.hardBlock && i.count > 0);
  const yellow = data.items.filter((i) => !i.affectsCycle && i.count > 0);
  // ก่อนถึงรอบปิดงวด (และยังไม่มีอะไรบล็อกจริง) → โทนสงบ ไม่เร่งเตือน.
  const earlyCalm = !data.dueNow && data.hardBlockCount === 0 && data.affectsCycleCount === 0;

  return (
    <>
      <PayrollPipeline current="close" period={period} />
      <div className="space-y-5 max-w-2xl">
        {data.mode === 'salary_only' && (
          <div className="card p-3 flex items-center gap-2.5 border-l-4 border-l-brand-400 bg-brand-50/60">
            <Wallet className="w-5 h-5 text-brand-600 shrink-0" />
            <p className="text-sm text-gray-700">
              โหมดเงินเดือนล้วน — คิดจากฐานเงินเดือน เงินเพิ่ม และรายการหัก ไม่นำการลงเวลา/ลา/OT มาคิด
            </p>
          </div>
        )}

        {data.ready ? (
          earlyCalm ? (
            <div className="card p-4 flex items-start gap-3 border-l-4 border-l-gray-300">
              <Clock className="w-6 h-6 text-gray-400 shrink-0" />
              <div>
                <p className="font-medium">ยังไม่ถึงรอบปิดงวด</p>
                <p className="text-sm text-gray-500">
                  ค่อยมาปิดงวดช่วงสิ้นเดือน (ราววันที่ {data.cutoffDay}) ตอนนี้ยังไม่มีอะไรต้องรีบ
                </p>
              </div>
            </div>
          ) : (
            <div className="card p-4 flex items-center gap-3 border-l-4 border-l-brand-500">
              <CheckCircle2 className="w-6 h-6 text-brand-600" />
              <div>
                <p className="font-medium">พร้อมปิดรอบ</p>
                <p className="text-sm text-gray-500">ไม่มีเรื่องค้างที่กระทบรอบเงินเดือน</p>
              </div>
            </div>
          )
        ) : data.hardBlockCount > 0 ? (
          <div className="card p-4 border-l-4 border-l-red-600 bg-red-50">
            <p className="font-medium text-red-700">ปิดงวดไม่ได้ — มี {data.hardBlockCount} รายการต้องแก้ก่อน</p>
            <p className="text-sm text-gray-600">ลืมทำเงินเดือน / ยังไม่ตั้งฐานเงินเดือน — แก้ให้ครบก่อน ปิดข้ามไม่ได้</p>
          </div>
        ) : (
          <div className="card p-4 border-l-4 border-l-red-500">
            <p className="font-medium text-red-700">มี {data.affectsCycleCount} เรื่องต้องเคลียร์ก่อนปิด</p>
            <p className="text-sm text-gray-500">ปิดได้แต่ต้องยืนยันพร้อมเหตุผล (บันทึกประวัติ)</p>
          </div>
        )}

        {hard.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-red-700">ต้องแก้ก่อน (ปิดข้ามไม่ได้)</h3>
            {hard.map((it) => <Row key={it.key} it={it} names={names} />)}
          </div>
        )}

        {red.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">ต้องเคลียร์ (กระทบรอบ)</h3>
            {red.map((it) => <Row key={it.key} it={it} names={names} />)}
          </div>
        )}

        {yellow.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">
              {earlyCalm ? 'ดูไว้ก่อนได้ (ค่อยจัดการตอนสิ้นงวด)' : 'ควรตรวจ (ไม่บล็อก)'}
            </h3>
            {yellow.map((it) => <Row key={it.key} it={it} names={names} />)}
          </div>
        )}
      </div>
    </>
  );
}

/** Standalone page kept for legacy /owner/close-readiness (now redirects). */
export function CloseReadiness() {
  return (
    <AdminLayout title="ความพร้อมปิดรอบ">
      <CloseReadinessContent />
    </AdminLayout>
  );
}
